import { Injectable } from '@angular/core';
import { HttpLogado } from '../HttpLogado';
import { SessionStorageService } from '../session-storage/session-storage.service';
import { environment } from '../../environments/environment';
import { LoginPBI } from '../../app/connection-wizard/connection-step-a/connection-step-a.component';
import convertHttpErrorToLocal from 'src/utils/convertHttpErrorToLocal';

export interface WorkspaceEntry {
  id: string;
  name: string;
  reportList: undefined | any[] | Promise<any[]>;
}

export interface ReportEntry {
  id: string;
  name: string,
  datasetId: string;
  embedUrl: string;
  reportType: "PowerBIReport" | string;
  webUrl: string;
  isOwnedByMe?: boolean;
}

@Injectable()
export class PBIService {
  constructor(
    private http: HttpLogado,
    private sessionStorage: SessionStorageService
  ) { }

  /**
   * How long should the pbi data remain in session before reloading it from server
   */
  readonly persistMinutes: number = 1;

  hasWorkspaceListCache() {
    return !!this.sessionStorage.getItem('workspace-list');
  }

  async getWorkspaceList(forced?: boolean): Promise<{cached: boolean, list: WorkspaceEntry[]}> {
    const cacheKey = `workspace-list`;

    if (forced) {
      this.sessionStorage.clearItem(cacheKey);
    }

    const cachedData = this.sessionStorage.getItem(cacheKey);
    if (cachedData && !forced) {
      const object = JSON.parse(cachedData);
      object.cached = true;
      return object;
    }

    try {
      const list: any = await this.http.get(`${environment.baseUrl}/pbi/workspaces`).toPromise();
      const object = {cached: false, list};
      const objectJSON = JSON.stringify(object);
      this.sessionStorage.setItem(cacheKey, objectJSON, this.persistMinutes / 60);
      return object;
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  async getWorkspaceReportList(workspaceId: string, forced?: boolean): Promise<{cached: boolean, list: ReportEntry[]}> {
    const cacheKey = `workspace-report-list-${workspaceId}`;

    if (forced) {
      this.sessionStorage.clearItem(cacheKey);
    }

    const cachedData = this.sessionStorage.getItem(cacheKey);
    if (cachedData && !forced) {
      const object = JSON.parse(cachedData);
      object.cached = true;
      return object;
    }

    try {
      const list: any = await this.http.get(`${environment.baseUrl}/pbi/workspaces/${workspaceId}/reports`).toPromise();
      const object = {cached: false, list};
      const objectJSON = JSON.stringify(object);
      this.sessionStorage.setItem(cacheKey, objectJSON, this.persistMinutes / 60);
      return object;
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  async getReportPages(workspaceId: string, reportId: string, forced: boolean): Promise<{cached: boolean, list: any[]}> {
    const cacheKey = `workspace-report-page-list-${workspaceId}-${reportId}`;

    if (forced) {
      this.sessionStorage.clearItem(cacheKey);
    }
    const cachedData = this.sessionStorage.getItem(cacheKey);
    if (cachedData && !forced) {
      const object = JSON.parse(cachedData);
      object.cached = true;
      return object;
    }

    try {
      const url = `${environment.baseUrl}/pbi/workspaces/${workspaceId}/reports/${reportId}/pages`;
      const list: any = await this.http.get(url).toPromise();
      const object = {cached: false, list};
      const objectJSON = JSON.stringify(object);
      this.sessionStorage.setItem(cacheKey, objectJSON, this.persistMinutes / 60);
      return object;
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  async getReportToken(workspaceId: string, reportId: string) {
    return await (this.http.get(`${environment.baseUrl}/pbi/workspaces/${workspaceId}/reports/${reportId}/tokenData`).toPromise());
  }

  async login(data: LoginPBI) {
    return await (this.http.post(`${environment.baseUrl}/pbi/login`, data).toPromise());
  }

  async register() {
    return await (this.http.post(`${environment.baseUrl}/pbi/login/appId`, {}).toPromise());
  }

  async checkConnection() {
    return await (this.http.get(`${environment.baseUrl}/pbi/connection/check`).toPromise());
  }

  private delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
